import { motion, useInView } from 'framer-motion';
import { Link } from "react-router-dom";
import { useRef } from 'react';
import { FaHandshake, FaLightbulb, FaShieldAlt, FaTools } from 'react-icons/fa';
import { HiChip, HiOutlineLightningBolt } from 'react-icons/hi';
import { MdConstruction, MdOutlineSecurity } from 'react-icons/md';
import { useNavigate } from "react-router-dom";
import Hero from "../assets/hero1.avif";
import heroimg from "../assets/missionimg.avif";
import apartImg from '../assets/abtimg2.webp';
import ClientsPartners from '../components/ClientsPartners';


const values = [
    {
        icon: <FaHandshake size={30} />,
        title: 'Client Commitment',
        description: 'We prioritize our clients’ success with tailored, impactful solutions.',
    },
    {
        icon: <FaLightbulb size={30} />,
        title: 'Technical Innovation',
        description: 'We integrate modern technologies to elevate construction and ICT services.',
    },
    {
        icon: <FaTools size={30} />,
        title: 'Operational Excellence',
        description: 'Disciplined execution and skilled teams ensure high-quality delivery.',
    },
    {
        icon: <FaShieldAlt size={30} />,
        title: 'Integrity & Safety',
        description: 'We maintain ethical practices and strict adherence to safety standards.',
    },
];


const features = [
    {
        icon: <MdConstruction size={30} />,
        title: 'Smart Construction',
        desc: 'Tech-enabled building practices for sustainable and efficient project delivery.',
    },
    {
        icon: <HiChip size={30} />,
        title: 'IoT Integration',
        desc: 'Real-time control, monitoring, and automation for smarter infrastructure systems.',
    },
    {
        icon: <MdOutlineSecurity size={30} />,
        title: 'Advanced Fire Systems',
        desc: 'Modern fire detection and suppression technologies with system intelligence.',
    },
    {
        icon: <HiOutlineLightningBolt size={30} />,
        title: 'Reliable Electrical Solutions',
        desc: 'Safe, scalable power and backup systems engineered for performance.',
    },
];

function useScrollAnimation({ amount = 0.2 } = {}) {
    const ref = useRef(null);
    const isInView = useInView(ref, { amount });
    return { ref, isInView };
}

function AboutPage() {
    const whoRef = useScrollAnimation();
    const missionRef = useScrollAnimation();
    const valuesRef = useScrollAnimation();
    const apartRef = useScrollAnimation();
    const welcomeRef = useScrollAnimation();

    const navigate = useNavigate();


    return (
        <div>
            <section
                className="relative w-full min-h-[40vh] bg-center bg-cover flex items-center justify-center px-4 py-20"
                style={{ backgroundImage: `url(${Hero})` }}
            >
                {/* Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-50 z-0" />

                {/* Content */}
                <div className="relative  z-10 max-w-5xl mx-auto text-center px-6">
                    <motion.h1
                        className="text-white text-[32px] sm:text-[48px]   font-extrabold leading-tight mb-4"
                        initial={{ opacity: 0, x: 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1 }}
                    >
                        About Altair
                    </motion.h1>

                    <motion.p
                        className="text-lg  text-gray-200 mb-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.7 }}
                    >
                        Leading Innovation Without Borders
                    </motion.p>

                    {/* Breadcrumb */}
                    <motion.nav
                        className="flex justify-center items-center space-x-2 text-sm sm:text-base text-gray-300"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 0.6 }}
                        aria-label="Breadcrumb"
                    >
                        <a href="/" className="hover:text-white transition-colors">Home</a>
                        <span className="text-gray-400">/</span>
                        <span className="text-white font-medium">About</span>
                    </motion.nav>
                </div>
            </section>


            <motion.section
                ref={whoRef.ref}
                className="py-16 w-full px-4 bg-gray-50"
                animate={whoRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 1, ease: 'easeOut' }}
            >
                <div>
                    <div className="max-w-6xl mx-auto">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800 mb-10">Who We Are</h2>
                        <div className="grid md:grid-cols-2 gap-8 text-gray-700 text-lg md:text-xl leading-relaxed">
                            <p>Altair is a multi-disciplinary service company dedicated to delivering superior solutions in construction & civil works, ICT & IoT, electrical installations, and fire protection systems. We serve businesses, institutions,
                                and industries seeking future-ready infrastructure....</p>
                            <p>With a commitment to quality, safety, and innovation, we provide end-to-end project execution — from planning and design to implementation and support. Our team is passionate about transforming spaces with smart,
                                sustainable, and secure systems.</p>
                        </div>
                    </div>
                </div>
            </motion.section>

            <section
                ref={missionRef.ref}
                className="relative w-full min-h-[60vh] bg-cover bg-center flex items-center overflow-hidden"
                style={{ backgroundImage: `url(${heroimg})` }}
            >
                {/* Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-50 z-0" />

                {/* Mission + Vision Content */}
                <motion.div
                    className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row justify-between gap-6 md:gap-10"
                    animate={missionRef.isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                >
                    {/* Mission Card */}
                    <div className="bg-white/10 backdrop-blur-md p-5 sm:p-8 md:p-10 rounded-xl shadow-lg flex-1 text-left text-white">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3">Our Mission</h2>
                        <p className="text-sm sm:text-base md:text-lg leading-relaxed">
                            To deliver reliable and integrated solutions across construction, technology, and safety systems —
                            empowering clients with quality infrastructure that’s built to last and adapt to the future.
                        </p>
                    </div>

                    {/* Vision Card */}
                    <div className="bg-white/10 backdrop-blur-md p-5 sm:p-8 md:p-10 rounded-xl shadow-lg flex-1 text-left text-white">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3">Our Vision</h2>
                        <p className="text-sm sm:text-base md:text-lg leading-relaxed">
                            To transform infrastructure and technology by delivering innovative, reliable, and secure solutions in
                            construction, ICT, electrical, and fire protection — shaping a future where progress is powered by safety,
                            connectivity, and sustainability.
                        </p>
                    </div>
                </motion.div>
            </section>

            <div
                ref={valuesRef.ref}
                className="conic-gradient(at center top, rgb(17, 24, 39), rgb(243, 244, 246), rgb(17, 24, 39)) py-16 w-full px-4 sm:px-6 lg:px-12"
            >
                <motion.div
                    className="max-w-6xl mx-auto"
                    animate={valuesRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 100 }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-gray-800 mb-12">Our Core Values</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ y: [0, -5, 0], transition: { repeat: Infinity, duration: 1 } }}
                                className="bg-white shadow-md rounded-2xl p-6 text-center cursor-pointer hover:shadow-lg hover:bg-orange-100"
                            >
                                <div className='flex flex-col items-start'>
                                    <div className="mb-4 text-orange-500">{value.icon}</div>
                                    <h3 className="text-sm sm:text-lg md:text-xl lg:text-2xl font-semibold mb-2 text-gray-700 text-start">{value.title}</h3>
                                    <p className="text-gray-600 text-sm sm:text-lg md:text-xl text-start">{value.description}</p>
                                </div>

                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>

            <motion.section
                ref={apartRef.ref}
                className="bg-gray-50 w-full py-16 px-4 sm:px-8 lg:px-16"
                animate={apartRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
                transition={{ duration: 0.8 }}
            >
                <div className="flex flex-col lg:flex-row max-w-6xl mx-auto">
                    {/* Image Column */}
                    <div className="w-full lg:w-1/2 flex">
                        <motion.img
                            src={apartImg}
                            alt="Our team at work"
                            className="w-full rounded-xl shadow-xl object-cover flex-1"
                            initial={{ scale: 0.95 }}
                            whileHover={{ scale: 1 }}
                            transition={{ duration: 0.4 }}
                        />
                    </div>

                    {/* Text Column */}
                    <div className="w-full lg:w-1/2 lg:pl-12 flex flex-col justify-center">
                        <p className="text-orange-600 text-sm uppercase font-semibold mb-2 tracking-wider">
                            What sets us apart
                        </p>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-8">
                            Building the Future with <span className="text-orange-600">Precision & Passion</span>
                        </h2>
                        <div className="grid grid-cols-1 gap-6">
                            {features.map((feat, idx) => (
                                <motion.div
                                    key={idx}
                                    whileHover={{ y: -4, boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}
                                    transition={{ duration: 0.3 }}
                                    className="flex items-start gap-4 p-4 hover:bg-orange-50"
                                >
                                    <div className="text-3xl bg-orange-600 rounded-full p-4">{feat.icon}</div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-800">{feat.title}</h3>
                                        <p className="text-sm text-gray-600">{feat.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.section>


            <motion.section
                ref={welcomeRef.ref}
                className="bg-white py-12 px-4 mb-16"
                animate={welcomeRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
                transition={{ duration: 0.8, type: "spring" }}
            >
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        className="bg-gradient-to-b from-gray-900 to-gray-600 rounded-2xl shadow-xl p-8 md:p-12"
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.7, type: "spring" }}
                    >
                        <motion.h1
                            className="text-3xl md:text-4xl font-extrabold mb-4 text-white"
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.3, duration: 0.7, type: "spring" }}
                        >
                            Welcome to Altair
                        </motion.h1>
                        <motion.p
                            className="text-gray-300 text-lg md:text-xl mb-6"
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.4, duration: 0.7, type: "spring" }}
                        >
                            We are dedicated to building the future with innovative engineering...
                        </motion.p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-start">
                            <Link
                                to="/contact"
                                className="w-full sm:w-auto px-4 py-2 sm:px-6 sm:py-3 rounded-xl border-2 font-medium sm:font-semibold text-base sm:text-lg bg-transparent border-white text-white transition-all duration-300 text-center"
                            >
                                Contact Us
                            </Link>
                        </div>

                    </motion.div>
                </div>
            </motion.section>
            <ClientsPartners/>
        </div>
    );
}

export default AboutPage;
